import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, LayoutGrid, LayoutTemplate, Play, Presentation as PresentationIcon, Users } from 'lucide-react'

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import routes from 'constants/routes'
import { cn } from 'lib/utils'

import { isPresentationsEnabled, presentationEditPath, presentationPresentPath } from '../featureFlag'
import * as presentationService from '../services/presentationService'
import type { AssetOption, PresentationScope, PresentationSummary } from '../types/presentation.types'
import { announceTemplateNotices, droppedFromDeck } from '../utils/templateNotices'
import { NewPresentationDialog, type FromTemplateValues } from './NewPresentationDialog'

/**
 * "Presentations" on an asset's page (MYD-497): the decks with a story about
 * this asset, each one a click from presenting, starting AT this asset's
 * story rather than at the deck's first slide.
 *
 * It also starts a new deck from a template for this asset (MYD-604), so it
 * shows even when the asset has no decks yet. It renders nothing when
 * Presentations is switched off, or when the decks could not be loaded: an
 * asset page must never break because of this menu.
 *
 * Only decks the viewer may open are listed (the server applies the same rule
 * as the gallery), and a partner's shared deck is marked as theirs.
 */
interface AssetPresentationsMenuProps {
	scope: PresentationScope
	entityId: string | undefined
	className?: string
}

export function AssetPresentationsMenu({ scope, entityId, className }: AssetPresentationsMenuProps) {
	const navigate = useNavigate()
	const location = useLocation()
	// Null until loaded, and after a failure.
	const [decks, setDecks] = useState<PresentationSummary[] | null>(null)
	// The asset, named, for the New deck dialog; set while it is open.
	const [newDeckFor, setNewDeckFor] = useState<AssetOption | null>(null)
	const [creating, setCreating] = useState(false)
	const [createError, setCreateError] = useState<string | null>(null)
	const enabled = isPresentationsEnabled()

	useEffect(() => {
		if (!enabled || !entityId) return
		let cancelled = false
		presentationService
			.listPresentations({ scope, entityId })
			.then(found => !cancelled && setDecks(found.filter(deck => deck.slideCount > 0)))
			.catch(() => !cancelled && setDecks(null))
		return () => {
			cancelled = true
		}
	}, [enabled, scope, entityId])

	if (!enabled || !entityId || decks === null) return null

	const present = (deck: PresentationSummary) =>
		navigate(`${presentationPresentPath(deck.id)}?anchor=${scope}:${entityId}`, {
			// Esc brings the presenter back here, to the asset.
			state: { from: `${location.pathname}${location.search}` },
		})

	// The dialog shows the asset by name, so look it up the way the editor does.
	const openNewDeck = async () => {
		setCreateError(null)
		const fallback: AssetOption = { scope, entityId, name: `this ${scope}`, subtitle: '' }
		try {
			const [found] = await presentationService.lookupAssets(scope, [entityId])
			setNewDeckFor(found ?? fallback)
		} catch {
			setNewDeckFor(fallback)
		}
	}

	const createFromTemplate = async ({ templateKey, asset, name }: FromTemplateValues) => {
		setCreating(true)
		setCreateError(null)
		try {
			const result = await presentationService.createPresentationFromTemplate({
				templateKey,
				anchor: { scope: asset.scope, entityId: asset.entityId },
				...(name ? { name } : {}),
			})
			setNewDeckFor(null)
			announceTemplateNotices(result, droppedFromDeck)
			navigate(presentationEditPath(result.presentation.id))
		} catch (cause: any) {
			setCreateError(cause?.response?.data?.message ?? 'The presentation could not be created.')
		} finally {
			setCreating(false)
		}
	}

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className={cn(
							'inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50',
							className,
						)}
					>
						<PresentationIcon className="h-3.5 w-3.5 text-primary" />
						{decks.length ? `Presentations (${decks.length})` : 'Presentations'}
						<ChevronDown className="h-3 w-3 text-gray-400" />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-72">
					{decks.length > 0 && (
						<>
							<DropdownMenuLabel className="text-xs font-normal text-gray-500">Present, starting at this {scope}'s story</DropdownMenuLabel>
							{decks.map(deck => (
								<DropdownMenuItem key={deck.id} onClick={() => present(deck)} className="gap-2">
									<Play className="h-3.5 w-3.5 shrink-0 text-primary" />
									<span className="min-w-0 flex-1 truncate">{deck.name}</span>
									{!deck.isOwner && (
										<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-gray-500" title="Shared with you by a partner">
											<Users className="h-3 w-3" /> Shared
										</span>
									)}
								</DropdownMenuItem>
							))}
							<DropdownMenuSeparator />
						</>
					)}
					<DropdownMenuItem onClick={openNewDeck} className="gap-2">
						<LayoutTemplate className="h-3.5 w-3.5 text-primary" /> New deck from template...
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => navigate(routes.PRESENTATIONS)} className="gap-2 text-gray-600">
						<LayoutGrid className="h-3.5 w-3.5" /> All presentations
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<NewPresentationDialog
				open={newDeckFor !== null}
				mode="create"
				presetAsset={newDeckFor}
				isSaving={creating}
				error={createError}
				onClose={() => setNewDeckFor(null)}
				// A blank deck is made from the gallery; from here, only templates.
				onSubmit={() => undefined}
				onCreateFromTemplate={createFromTemplate}
			/>
		</>
	)
}

export default AssetPresentationsMenu
